import { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { find } from 'lodash-es';
import {
    Dropdown,
    HStack,
    Text,
    ListViewIcon,
    GridViewIcon,
    CompactViewIcon,
    StockListViewIcon,
} from '@library';
import type { ProductsViewMode } from '../../../types';
import useAvailableViewModes from '../../../hooks/useAvailableViewModes';

import styles from './ProductListControls.module.css';

const buttonProps = {
    rounded: true,
    text: true,
    'data-test-class': 'viewModeButton',
};

const ProductListViewControls = ({
    viewMode,
    setViewMode,
}: {
    viewMode: ProductsViewMode;
    setViewMode: (value: ProductsViewMode) => void;
}) => {
    const viewModes = useAvailableViewModes();
    const intl = useIntl();

    const viewModesButtons = useMemo(
        () =>
            [
                {
                    value: 'grid',
                    icon: <GridViewIcon micro />,
                    label: intl.formatMessage({ id: 'tile view' }),
                },
                {
                    value: 'table',
                    icon: <CompactViewIcon micro />,
                    label: intl.formatMessage({
                        id: 'ProductList ViewMode Table',
                    }),
                },
                {
                    value: 'list',
                    icon: <ListViewIcon micro />,
                    label: intl.formatMessage({ id: 'list view' }),
                },
                {
                    value: 'stockList',
                    icon: <StockListViewIcon micro />,
                    label: intl.formatMessage({ id: 'stock list' }),
                },
            ].filter(item =>
                viewModes.includes(item.value as ProductsViewMode)
            ),
        [intl, viewModes]
    );

    const selectedMode = find(viewModesButtons, { value: viewMode });

    return (
        <Dropdown
            buttonProps={{
                theme: styles.button,
                ...buttonProps,
            }}
            items={viewModesButtons}
            placement={'bottom-end'}
            matchWidth={140}
            onSelect={value => setViewMode(value as ProductsViewMode)}
        >
            <HStack gap="gap8" align="center">
                {selectedMode?.icon && <div>{selectedMode.icon}</div>}
                <Text>{selectedMode?.label}</Text>
            </HStack>
        </Dropdown>
    );
};

export default memo(ProductListViewControls);
